import * as React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";

import { AppLayout } from "@/components/layout/AppLayout";
import { SEOHead, useSEO } from "@/components/common/SEOHead";
import { useStore, StoreProvider } from "@/context/StoreContext";

// Eager load main page for fast LCP
import StorePage from "@/pages/StorePage";

// Eager load SplashScreen - critical for first paint with logo
import { SplashScreen } from "@/components/SplashScreen";

// Lazy load other pages - improves TBT and initial bundle size
const HomePage = React.lazy(() => import("@/pages/HomePage"));
const CategoriesPage = React.lazy(() => import("@/pages/CategoriesPage"));
const LibraryPage = React.lazy(() => import("@/pages/LibraryPage"));
const AdminPage = React.lazy(() => import("@/pages/AdminPage"));
const StaticPage = React.lazy(() => import("@/pages/StaticPage"));
const GamePage = React.lazy(() => import("@/pages/GamePage"));
const ProgramsPage = React.lazy(() => import("@/pages/ProgramsPage"));
const WebsitesPage = React.lazy(() => import("@/pages/WebsitesPage"));
const WebsiteDetailPage = React.lazy(() => import("@/pages/WebsiteDetailPage"));
const GiveawayPage = React.lazy(() => import("@/pages/GiveawayPage"));
const CanMyPCRunItPage = React.lazy(() => import("@/pages/CanMyPCRunItPage"));
const CompareGamesPage = React.lazy(() => import("@/pages/CompareGamesPage"));
const GameRecommendPage = React.lazy(() => import("@/pages/GameRecommendPage"));

// Google Site Verification Component - serves verification file content
function GoogleVerification() {
  const { filename } = useParams();
  const [content, setContent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const fetchVerification = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_URL}/api/google-verification/${filename}`);
        if (response.ok) {
          const text = await response.text();
          // Replace entire document with verification content
          document.open();
          document.write(text);
          document.close();
        } else {
          setContent('File not found');
          setLoading(false);
        }
      } catch (error) {
        setContent('Error loading verification file');
        setLoading(false);
      }
    };
    
    if (filename) {
      fetchVerification();
    }
  }, [filename]);
  
  if (loading) return null;
  return <div>{content}</div>;
}

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Global SEO Component
function GlobalSEO() {
  const seo = useSEO();
  
  if (!seo) return null;
  
  return (
    <SEOHead
      title={seo.seoTitle || seo.siteTitle || "CrackOyun"}
      description={seo.seoDescription || seo.siteDescription}
      keywords={seo.seoKeywords}
      canonicalUrl={seo.canonicalUrl}
      ogImage={seo.ogImage}
      noIndex={seo.robotsIndex === false}
      noFollow={seo.robotsFollow === false}
      googleAnalytics={seo.googleAnalytics}
      googleVerification={seo.googleVerification}
    />
  );
}

// Splash screen wrapper that reads theme from store - MUST be inside StoreProvider
function SplashScreenWithTheme({ onComplete }) {
  const { settings } = useStore();
  const splashTheme = settings?.splashTheme || 'cyber-neon'; // Default: cyber neon theme
  return <SplashScreen onComplete={onComplete} themeId={splashTheme} />;
}

function App() {
  // Splash screen is handled by HTML in index.html
  // React hides it when ready
  React.useEffect(() => {
    const htmlSplash = document.getElementById('splash-screen');
    if (htmlSplash && htmlSplash.style.display !== 'none') {
      // Wait for loading bar animation to complete (2.2s) + buffer
      setTimeout(() => {
        htmlSplash.classList.add('splash-exit');
        setTimeout(() => {
          htmlSplash.style.display = 'none';
        }, 400);
      }, 2200);
    }
  }, []);

  // Default to dark mode for the launcher vibe.
  React.useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  // Scroll performance optimization - disable animations during scroll
  // This makes desktop as smooth as mobile
  React.useEffect(() => {
    let scrollTimeout;
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        // Immediately add class on first scroll event
        document.documentElement.classList.add('is-scrolling');
        ticking = true;
        
        requestAnimationFrame(() => {
          ticking = false;
        });
      }
      
      // Remove class after scroll stops
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        document.documentElement.classList.remove('is-scrolling');
      }, 150);
    };
    
    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also handle wheel events for smoother response
    window.addEventListener('wheel', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Dynamic Favicon Component
  const DynamicFavicon = () => {
    const { favicon } = useStore();
    
    React.useEffect(() => {
      if (favicon) {
        const faviconUrl = favicon.startsWith('/') 
          ? `${process.env.REACT_APP_BACKEND_URL}${favicon}` 
          : favicon;
        
        // Update or create favicon link
        let link = document.querySelector("link[rel*='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
        
        // Also update apple-touch-icon if exists
        let appleLink = document.querySelector("link[rel='apple-touch-icon']");
        if (appleLink) {
          appleLink.href = faviconUrl;
        }
      }
    }, [favicon]);
    
    return null;
  };

  return (
    <BrowserRouter>
      <HelmetProvider>
        <StoreProvider>
          
          {/* Global SEO Settings */}
          <GlobalSEO />
          
          {/* Dynamic Favicon */}
          <DynamicFavicon />

          <Routes>
          {/* Google Site Verification - Must be outside AppLayout */}
          <Route path="/google:filename.html" element={<GoogleVerification />} />
          
          <Route element={<AppLayout />}>
            {/* New main landing as requested */}
            <Route path="/" element={<StorePage />} />

            {/* Programs page */}
            <Route path="/programlar" element={
              <React.Suspense fallback={<PageLoader />}>
                <ProgramsPage />
              </React.Suspense>
            } />

            {/* Websites page */}
            <Route path="/siteler" element={
              <React.Suspense fallback={<PageLoader />}>
                <WebsitesPage />
              </React.Suspense>
            } />
            <Route path="/site/:slug" element={
              <React.Suspense fallback={<PageLoader />}>
                <WebsiteDetailPage />
              </React.Suspense>
            } />

            {/* Game detail page */}
            <Route path="/cekilis" element={
              <React.Suspense fallback={<PageLoader />}>
                <GiveawayPage />
              </React.Suspense>
            } />

            <Route path="/bilgisayarim-acar-mi" element={
              <React.Suspense fallback={<PageLoader />}>
                <CanMyPCRunItPage />
              </React.Suspense>
            } />

            <Route path="/karsilastir" element={
              <React.Suspense fallback={<PageLoader />}>
                <CompareGamesPage />
              </React.Suspense>
            } />

            <Route path="/oyun-oneri" element={
              <React.Suspense fallback={<PageLoader />}>
                <GameRecommendPage />
              </React.Suspense>
            } />

            <Route path="/:slug" element={
              <React.Suspense fallback={<PageLoader />}>
                <GamePage />
              </React.Suspense>
            } />

            {/* Existing prototype pages kept (can be removed if you want) */}
            <Route path="/prototype" element={
              <React.Suspense fallback={<PageLoader />}>
                <HomePage />
              </React.Suspense>
            } />
            <Route path="/categories" element={
              <React.Suspense fallback={<PageLoader />}>
                <CategoriesPage />
              </React.Suspense>
            } />
            <Route path="/library" element={
              <React.Suspense fallback={<PageLoader />}>
                <LibraryPage />
              </React.Suspense>
            } />
          </Route>

          {/* Admin page - standalone without AppLayout */}
          <Route path="/admin" element={
            <React.Suspense fallback={<PageLoader />}>
              <AdminPage />
            </React.Suspense>
          } />
          
          {/* Static pages (about, privacy, terms, contact) */}
          <Route path="/page/:slug" element={
            <React.Suspense fallback={<PageLoader />}>
              <StaticPage />
            </React.Suspense>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* Toast notifications */}
        <Toaster position="top-right" richColors closeButton />
        </StoreProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
}

export default App;
